
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";

import LeftMenu from "../../components/left-menu";

import {
  Grid,
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  FormControlLabel,
  Switch,
} from "@mui/material";

const LEGAL_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/legal`;

function normalizeText(value = "") {
  return String(value || "").trim();
}

function slugify(value = "") {
  return normalizeText(value)
    .toLowerCase()
    .replace(/&/g, " dan ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function getStageLabel(stage) {
  return normalizeText(stage?.label || stage?.name);
}

function ensureDoneStageNames(lines = []) {
  const clean = [];
  const seen = new Set();

  (Array.isArray(lines) ? lines : []).forEach((item) => {
    const value = normalizeText(item);
    if (!value) return;

    const key = slugify(value);
    if (!key || seen.has(key)) return;

    seen.add(key);
    clean.push(value);
  });

  const withoutDone = clean.filter((item) => slugify(item) !== "done");
  withoutDone.push("Done");

  return withoutDone;
}

function formFromTemplate(template) {
  if (!template) {
    return {
      name: "",
      description: "",
      isActive: true,
      stagesText: "Start\nDone",
    };
  }

  return {
    name: normalizeText(template?.name),
    description: normalizeText(template?.description),
    isActive: template?.isActive !== false,
    stagesText: (Array.isArray(template?.stages) ? template.stages : [])
      .map((stage) => getStageLabel(stage))
      .join("\n"),
  };
}

function extractListFromResponse(resp, keys = ["items", "data"]) {
  const data = resp?.data || {};

  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }

  return [];
}

function extractErrorMessage(error, fallback = "Terjadi kesalahan.") {
  return error?.response?.data?.message || error?.message || fallback;
}

function logAxiosError(label, error) {
  console.error(
    label,
    {
      message: error?.message,
      status: error?.response?.status,
      method: error?.config?.method,
      url: error?.config?.url,
      params: error?.config?.params,
      data: error?.response?.data,
    },
    error
  );
}

export default function LegalStagesEdit() {
  const reduxUser = useSelector((state) => state?.user || {});
  const nestedUser = reduxUser?.user || {};

  const actorName =
    normalizeText(reduxUser?.full_name) ||
    normalizeText(reduxUser?.sales) ||
    normalizeText(reduxUser?.name) ||
    normalizeText(reduxUser?.username) ||
    normalizeText(nestedUser?.full_name) ||
    normalizeText(nestedUser?.sales) ||
    normalizeText(nestedUser?.name) ||
    normalizeText(nestedUser?.username) ||
    "System";

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  const [form, setForm] = useState(formFromTemplate(null));

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorText, setErrorText] = useState("");

  const selectedTemplate = useMemo(() => {
    return templates.find((item) => item?._id === selectedTemplateId) || null;
  }, [templates, selectedTemplateId]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setErrorText("");

      console.log("[LEGAL FE][StagesEdit][loadTemplates][REQ]");

      const resp = await axios.get(`${LEGAL_BASE_URL}/templates`, {
        params: {
          includeInactive: true,
        },
      });

      console.log("[LEGAL FE][StagesEdit][loadTemplates][RES]", resp?.data);

      const items = extractListFromResponse(resp);
      setTemplates(items);

      if (items.length > 0) {
        setSelectedTemplateId(items[0]._id);
        setForm(formFromTemplate(items[0]));
        setIsCreatingNew(false);
      } else {
        setSelectedTemplateId("");
        setForm(formFromTemplate(null));
        setIsCreatingNew(true);
      }
    } catch (error) {
      logAxiosError("[LEGAL FE][StagesEdit][loadTemplates][ERR]", error);
      setErrorText(
        extractErrorMessage(error, "Gagal memuat master layanan legal.")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const handleCreateNew = () => {
    console.log("[LEGAL FE][StagesEdit][Create New]");
    setIsCreatingNew(true);
    setSelectedTemplateId("");
    setForm(formFromTemplate(null));
    setErrorText("");
  };

  const handleSelectTemplate = (template) => {
    console.log("[LEGAL FE][StagesEdit][Select Template]", template);
    setIsCreatingNew(false);
    setSelectedTemplateId(template?._id || "");
    setForm(formFromTemplate(template));
    setErrorText("");
  };

  const handleSave = async () => {
    const name = normalizeText(form?.name);
    const description = normalizeText(form?.description);

    const lines = String(form?.stagesText || "")
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);

    if (!name) {
      setErrorText("Nama layanan wajib diisi.");
      return;
    }

    if (lines.length === 0) {
      setErrorText("Minimal isi 1 stage.");
      return;
    }

    const normalizedStageNames = ensureDoneStageNames(lines);
    const stages = normalizedStageNames.map((label, index) => ({
      key: slugify(label) || `stage-${index + 1}`,
      label,
      order: index + 1,
      defaultResponsibility: "",
    }));

    const payload = {
      name,
      key: isCreatingNew
        ? slugify(name)
        : normalizeText(selectedTemplate?.key) || slugify(name),
      description,
      isActive: !!form?.isActive,
      stages,
      actorName,
    };

    console.log("[LEGAL FE][StagesEdit][Save][REQ]", payload);

    try {
      setSaving(true);
      setErrorText("");

      if (isCreatingNew) {
        const resp = await axios.post(`${LEGAL_BASE_URL}/templates`, payload);
        console.log("[LEGAL FE][StagesEdit][Create][RES]", resp?.data);
      } else {
        const resp = await axios.put(
          `${LEGAL_BASE_URL}/templates/${selectedTemplate?._id}`,
          payload
        );
        console.log("[LEGAL FE][StagesEdit][Update][RES]", resp?.data);
      }

      await loadTemplates();
    } catch (error) {
      logAxiosError("[LEGAL FE][StagesEdit][Save][ERR]", error);
      setErrorText(extractErrorMessage(error, "Gagal menyimpan template."));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!selectedTemplate?._id) return;

    const confirmed = window.confirm(
      `Nonaktifkan template "${selectedTemplate?.name}"?`
    );

    if (!confirmed) return;

    try {
      console.log("[LEGAL FE][StagesEdit][Deactivate][REQ]", {
        id: selectedTemplate._id,
      });

      const resp = await axios.delete(
        `${LEGAL_BASE_URL}/templates/${selectedTemplate._id}`,
        {
          data: {
            actorName,
          },
        }
      );

      console.log("[LEGAL FE][StagesEdit][Deactivate][RES]", resp?.data);

      await loadTemplates();
    } catch (error) {
      logAxiosError("[LEGAL FE][StagesEdit][Deactivate][ERR]", error);
      setErrorText(
        extractErrorMessage(error, "Gagal menonaktifkan template.")
      );
    }
  };

  return (
    <Grid container>
      <Grid item xl={0.5} lg={0.7} md={0.9} sm={3.2}>
        <LeftMenu />
      </Grid>

      <Grid item xl={10.8} lg={10.6} md={10.4} sm={8.8} xs={12}>
        <Box sx={{ mt: 15 }} />

        <Box sx={{ px: { xs: 2, md: 3 }, pb: 5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
              flexWrap: "wrap",
              mb: 3,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: { xs: 28, md: 38 }, fontWeight: 700 }}>
                Master Layanan Legal
              </Typography>

              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                Edit kebutuhan, edit urutan stage, aktif/nonaktifkan template.
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1.2, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                onClick={() => {
                  window.location.href = "/#/legaltodolist";
                }}
              >
                Buka Dashboard
              </Button>

              <Button variant="outlined" onClick={loadTemplates}>
                Refresh
              </Button>
            </Box>
          </Box>

          {!!errorText && (
            <Typography color="error" sx={{ mb: 2 }}>
              {errorText}
            </Typography>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                <Box sx={{ p: 2 }}>
                  <Button variant="contained" fullWidth onClick={handleCreateNew}>
                    Tambah Layanan Baru
                  </Button>
                </Box>

                {loading ? (
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Typography color="text.secondary">
                      Loading template...
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ pt: 0 }}>
                    {templates.map((template) => (
                      <ListItemButton
                        key={template?._id}
                        selected={
                          !isCreatingNew && selectedTemplateId === template?._id
                        }
                        onClick={() => handleSelectTemplate(template)}
                      >
                        <ListItemText
                          primary={template?.name || "-"}
                          secondary={`${template?.isActive !== false ? "Aktif" : "Nonaktif"} • ${
                            Array.isArray(template?.stages) ? template.stages.length : 0
                          } stage`}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
                <Typography sx={{ fontWeight: 700, mb: 2, fontSize: 20 }}>
                  {isCreatingNew ? "Buat layanan baru" : "Edit layanan"}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nama layanan"
                      value={form?.name}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          name: event.target.value,
                        }))
                      }
                    />
                  </Grid>

                  {!isCreatingNew && !!selectedTemplate && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Key internal"
                        value={selectedTemplate?.key || ""}
                        disabled
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Deskripsi singkat"
                      value={form?.description}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          description: event.target.value,
                        }))
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!form?.isActive}
                          onChange={(event) =>
                            setForm((prev) => ({
                              ...prev,
                              isActive: event.target.checked,
                            }))
                          }
                        />
                      }
                      label={form?.isActive ? "Template aktif" : "Template nonaktif"}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={12}
                      label="Daftar stage (1 baris = 1 stage)"
                      helperText='Tulis stage per baris. "Done" akan otomatis dipaksa selalu ada di paling bawah.'
                      value={form?.stagesText}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          stagesText: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    mt: 2.5,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    {!isCreatingNew && !!selectedTemplate && (
                      <Button
                        color="error"
                        variant="outlined"
                        onClick={handleDeactivate}
                      >
                        Nonaktifkan Template
                      </Button>
                    )}
                  </Box>

                  <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : "Save Template"}
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );
}
